import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Chip } from '../onboarding/Choices';
import {
  PROPERTY_INTERESTS,
  TICKET_RANGES,
  OBJECTIVES,
  EXPERIENCE_LEVELS,
} from '../../lib/investorProfileOptions';

/** Everything the investor told us at onboarding, changeable without repeating it. */
export const PreferencesSection = () => {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get('/onboarding/profile/state')
      .then(({ data }) =>
        setPrefs({ property_interests: [], marketing_consent: false, ...(data.preferences || {}) })
      )
      .catch(() => setPrefs({ property_interests: [], marketing_consent: false }));
  }, []);

  if (!prefs) return null;

  const set = (patch) => setPrefs((prev) => ({ ...prev, ...patch }));
  const toggleInterest = (value) =>
    set({
      property_interests: prefs.property_interests.includes(value)
        ? prefs.property_interests.filter((v) => v !== value)
        : [...prefs.property_interests, value],
    });

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/onboarding/profile/preferences', prefs);
      toast.success('Preferences updated');
    } catch (error) {
      toast.error('Could not save your preferences');
    } finally {
      setSaving(false);
    }
  };

  const group = (label, options, selected, onPick, prefix) => (
    <div>
      <p className="text-sm font-medium mb-3">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const value = option.value || option;
          const text = option.label || option;
          return (
            <Chip
              key={value}
              selected={Array.isArray(selected) ? selected.includes(value) : selected === value}
              onClick={() => onPick(value)}
              testId={`${prefix}-${value}`}
            >
              {text}
            </Chip>
          );
        })}
      </div>
    </div>
  );

  return (
    <Card data-testid="investment-preferences-card">
      <CardHeader>
        <CardTitle className="text-lg">Investment Preferences</CardTitle>
        <CardDescription>
          These shape what we show you. They are not an assessment of what is suitable for you.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-7">
        {group('Types of real estate', PROPERTY_INTERESTS, prefs.property_interests, toggleInterest, 'pref-interest')}
        {group('Typical amount per project', TICKET_RANGES, prefs.ticket_range, (v) => set({ ticket_range: v }), 'pref-ticket')}
        {group('Primary objective', OBJECTIVES, prefs.objective, (v) => set({ objective: v }), 'pref-objective')}
        {group('Experience', EXPERIENCE_LEVELS, prefs.experience, (v) => set({ experience: v }), 'pref-experience')}

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={prefs.marketing_consent}
            onChange={(e) => set({ marketing_consent: e.target.checked })}
            className="mt-1 w-4 h-4 rounded border-black/20 text-primary focus:ring-primary"
            data-testid="pref-marketing-consent"
          />
          <span className="text-sm text-foreground-muted">
            Email me new projects and updates.
          </span>
        </label>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="save-preferences-button">
            {saving ? 'Saving...' : 'Save Preferences'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
